import { BinanceBaseUrlKey, GenericAPIResponse, getRestBaseUrl, RestClientOptions, USDMClient } from "libs/binance-api/src";
import { MarketError } from "../error/codes";
import { firstValueFrom, tap, defer, retry, throwError, timer, switchMap, catchError } from "rxjs";
import { MarketLoggerService, RestRequestLogRow } from "./market-logger.service";
import { isEmpty } from "lodash-es";
import { AxiosRequestConfig } from "axios";
import { BinanceUsdmTimeService } from "./market-time.service";

export class BinanceUsdmRestClient extends USDMClient {

  static timeService?: BinanceUsdmTimeService

  constructor(
    restClientOptions: RestClientOptions = {},
    requestOptions: AxiosRequestConfig = {},
    private marketLogger: MarketLoggerService,
  ) {
    super(restClientOptions, requestOptions)
  }

  /**
   * Заменяем метод синхронизации времени
   */
  override syncTime(): Promise<any> {
    if (BinanceUsdmRestClient.timeService) {
      return firstValueFrom(BinanceUsdmRestClient.timeService.offset$.pipe(
        tap(offset => this.setTimeOffset(offset))
      ))
    }
    throw new Error('Time offset observable not initialized')
  }

  /**
   * Логирует ответ от биржи в market log
   */
  private logResponse(url: string, params: any) {
    const {url: parsedUrl, params: queryParams} = this.parseUrl(url)
    params = typeof(params) == 'object' ? params : {}
    const data = {...queryParams, ...params, signature: undefined, timestamp: undefined, recvWindow: undefined}
    const logRow: RestRequestLogRow = {
      time: Date.now(),
      market: 'binance',
      type: 'rest',
      url: parsedUrl,
      params: isEmpty(data) ? undefined : data,
      httpCode: 200,
      apiCode: 0,
      apiMessage: ''
    }
    this.marketLogger.log(logRow)
  }

  /**
   * Логирует ошибку от биржи в weblog
   */
  private logError(e: any) {
    if (e && this.logFilter(e)) {
      // Это ошибка от биржи сконвертированная либой
      if ('requestUrl' in e && 'httpStatus' in e && 'body' in e) {
        const {url: parsedUrl, params: queryParams} = this.parseUrl(e.requestUrl)
        const bodyParams = typeof(e.requestBody) == 'object' ? e.requestBody : {}
        const data = {...queryParams, ...bodyParams, signature: undefined, timestamp: undefined, recvWindow: undefined}
        const logRow: RestRequestLogRow = {
          time: Date.now(),
          market: 'binance',
          type: 'rest',
          url: parsedUrl,
          params: isEmpty(data) ? undefined : data,
          httpCode: e.httpStatus,
          apiCode: e.body.code,
          apiMessage: e.body.msg
        }
        this.marketLogger.log(logRow)
      }
    }
  }

  /**
   * Конвертирует ошибку биржи в MarketError
   */
  private convertError(e: any): any {
    if ('body' in e && 'code' in e.body && 'msg' in e.body) {
      // Это ошибка api binance
      return new MarketError(e.body.code, e.body.msg)
    }
    return e
  }

  /**
   * Фильтрует ошибку для логирования в weblog
   */
  private logFilter(e: any) {
    if (typeof(e.requestUrl) == 'string' && (e.requestUrl as string).includes('fapi/v1/order/test')) {
      return false
    }
    return true
  }

  private parseUrl(url: string): {url: string, params: Record<string, any> | undefined} {
    try {
      const objUrl = new URL(url)
      const queryParams = Object.fromEntries(objUrl.searchParams.entries())
      return {url: objUrl.origin + objUrl.pathname, params: queryParams}
    } catch {
      const baseUrl = getRestBaseUrl('usdm', {})
      return {url: `${baseUrl}/${url}`, params: undefined}
    }
  }

  private executeRequest(endpoint: string, params: any, callback: () => Promise<any>): Promise<any> {
    return firstValueFrom(
      defer(() => callback()).pipe(
        retry({
          count: 2,
          delay: error => {
            const convertedError = this.convertError(error)
            if (convertedError instanceof MarketError && convertedError.apiCode === '-1021' && BinanceUsdmRestClient.timeService) {
              return timer(1000).pipe(
                switchMap(() => BinanceUsdmRestClient.timeService!.fetchTimeOffset()),
                tap(offset => this.setTimeOffset(offset))
              )
            }
            return throwError(() => convertedError)
          }
        }),
        tap(() => this.logResponse(endpoint, params)),
        catchError(error => {
          this.logError(error)
          return throwError(() => error)
        })
      )
    )
  }

  override async get(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.get(endpoint, params))
  }

  override async getForBaseUrl(endpoint: string, baseUrlKey: BinanceBaseUrlKey, params?: any) {
    return await this.executeRequest(endpoint, params, () => super.getForBaseUrl(endpoint, baseUrlKey, params))
  }

  override async getPrivate(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.getPrivate(endpoint, params))
  }

  override async post(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.post(endpoint, params))
  }

  override async postPrivate(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.postPrivate(endpoint, params))
  }

  override async put(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.put(endpoint, params))
  }

  override async putPrivate(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.putPrivate(endpoint, params))
  }

  override async delete(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.delete(endpoint, params))
  }

  override async deletePrivate(endpoint: string, params?: any): GenericAPIResponse {
    return await this.executeRequest(endpoint, params, () => super.deletePrivate(endpoint, params))
  }

}
